// src/components/admin/analytics/OverviewTab.jsx
import { useState, useEffect } from 'react';
import { collection, query, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  FaDollarSign,
  FaFileInvoice,
  FaEnvelope,
  FaChartLine,
} from 'react-icons/fa';
import { FaArrowTrendUp, FaArrowTrendDown } from 'react-icons/fa6';
import useRevenueAnalytics from '../../../hooks/useRevenueAnalytics';

const fmt = (val) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(val);

const KpiCard = ({ title, value, sub, icon: Icon, color, bgColor, change }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`${bgColor} p-3 rounded-full`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      {change !== undefined && (
        <div
          className={`flex items-center text-sm font-medium ${
            change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {change >= 0 ? (
            <FaArrowTrendUp className="w-4 h-4 mr-1" />
          ) : (
            <FaArrowTrendDown className="w-4 h-4 mr-1" />
          )}
          {Math.abs(change)}%
        </div>
      )}
    </div>
    <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow p-6 animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 bg-gray-200 rounded-full" />
    </div>
    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
    <div className="h-7 bg-gray-200 rounded w-1/2" />
  </div>
);

const OverviewTab = () => {
  const { stats, loading: revenueLoading } = useRevenueAnalytics();
  const [quoteCount, setQuoteCount] = useState(0);
  const [quoteLoading, setQuoteLoading] = useState(true);

  useEffect(() => {
    const loadQuotes = async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const q = query(
          collection(db, 'quotes'),
          where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo))
        );
        const snap = await getDocs(q);
        setQuoteCount(snap.size);
      } catch {
        // quotes collection may not exist yet — default to 0
        setQuoteCount(0);
      } finally {
        setQuoteLoading(false);
      }
    };
    loadQuotes();
  }, []);

  const loading = revenueLoading || quoteLoading;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="bg-white rounded-lg shadow p-6 animate-pulse h-80" />
      </div>
    );
  }

  const currentMonthTrend = stats.monthlyTrend[5] || { revenue: 0, invoices: 0 };

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          title="Revenue This Month"
          value={fmt(stats.monthlyRevenue)}
          sub={`vs ${fmt(stats.lastMonthRevenue)} last month`}
          icon={FaDollarSign}
          color="text-green-600"
          bgColor="bg-green-100"
          change={stats.monthlyChange}
        />
        <KpiCard
          title="Outstanding Balance"
          value={fmt(stats.totalOutstanding)}
          sub={`${stats.openCount} unpaid invoice${stats.openCount !== 1 ? 's' : ''}`}
          icon={FaFileInvoice}
          color="text-yellow-600"
          bgColor="bg-yellow-100"
        />
        <KpiCard
          title="Quote Requests (30d)"
          value={String(quoteCount)}
          sub="from contact/quote form"
          icon={FaEnvelope}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <KpiCard
          title="Payment Rate"
          value={`${stats.paymentRate}%`}
          sub={`${stats.paidCount} of ${stats.totalInvoices} invoices paid`}
          icon={FaChartLine}
          color="text-purple-600"
          bgColor="bg-purple-100"
          change={stats.paymentRate - 80}
        />
      </div>

      {/* Revenue Trend */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Revenue — Last 6 Months</h3>
          <span className="text-sm text-gray-500">Paid invoices only</span>
        </div>
        {stats.monthlyTrend.every((m) => m.revenue === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FaChartLine className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium text-gray-500">No paid invoices recorded yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Revenue will appear here as invoices are paid
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              data={stats.monthlyTrend}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
              />
              <Tooltip
                formatter={(val) => [fmt(val), 'Revenue']}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#16a34a"
                strokeWidth={2.5}
                fill="url(#revenueGradient)"
                dot={{ fill: '#16a34a', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Bottom Summary Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Status Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Invoice Breakdown</h3>
          <div className="space-y-3">
            {[
              { label: 'Total Sent', value: stats.totalInvoices, color: 'bg-gray-100 text-gray-700' },
              { label: 'Paid', value: stats.paidCount, color: 'bg-green-100 text-green-700' },
              { label: 'Awaiting Payment', value: stats.openCount, color: 'bg-yellow-100 text-yellow-700' },
              { label: 'Failed', value: stats.failedCount, color: 'bg-red-100 text-red-700' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{label}</span>
                <span className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${color}`}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue Snapshot */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Revenue Snapshot</h3>
          <div className="space-y-3">
            {[
              { label: 'All-Time Collected', value: fmt(stats.totalRevenue) },
              { label: 'This Month', value: fmt(stats.monthlyRevenue) },
              { label: 'Last Month', value: fmt(stats.lastMonthRevenue) },
              { label: 'Avg. Invoice Value', value: fmt(stats.avgInvoice) },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{label}</span>
                <span className="text-sm font-semibold text-gray-900">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* This Month at a Glance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">This Month</h3>
          {currentMonthTrend.invoices === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-gray-400">
              <FaFileInvoice className="w-8 h-8 mb-2" />
              <p className="text-sm">No invoices sent this month</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-2">
                <p className="text-5xl font-bold text-gray-900">{currentMonthTrend.invoices}</p>
                <p className="text-sm text-gray-500 mt-1">
                  invoice{currentMonthTrend.invoices !== 1 ? 's' : ''} sent
                </p>
              </div>
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Collected</span>
                  <span className="font-semibold text-green-700">{fmt(stats.monthlyRevenue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Outstanding</span>
                  <span className="font-semibold text-yellow-600">{fmt(stats.totalOutstanding)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;